import { Button, Modal, useToast } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage, listAgentsOptions, type SpaceQuickActionSummary } from "@/api";
import { useAppForm } from "@/components/form/form";

export interface QuickActionValues {
  label: string;
  description: string;
  agentUuid: string;
  promptTemplate: string;
  autoSubmit: boolean;
}

/** The quick action starts a chat with the default assistant (no agent). */
export const ASSISTANT = "assistant";

/** Add or edit a quick action (an agent or the assistant, + optional prompt). Mount when open. */
export function QuickActionModal({
  action,
  onSave,
  onClose,
}: Readonly<{
  action?: SpaceQuickActionSummary;
  onSave: (values: QuickActionValues) => Promise<unknown>;
  onClose: () => void;
}>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.quickActions" });
  const toast = useToast();
  const agents = useQuery({ ...listAgentsOptions({ query: { page_size: 100 } }), select: (r) => r.data });
  // The default assistant can use the space's connectors and skills; agents use their own tools.
  const options = [
    { value: ASSISTANT, label: t("form.assistant") },
    ...(agents.data ?? []).map((a) => ({ value: a.uuid, label: a.name })),
  ];
  // Keep an agent the editor can't see listed, so editing other fields doesn't drop it.
  if (action?.agentUuid && !options.some((o) => o.value === action.agentUuid)) {
    options.unshift({ value: action.agentUuid, label: action.agentName ?? action.agentUuid });
  }
  const schema = z.object({
    label: z.string().trim().min(1, t("form.labelRequired")).max(255),
    description: z.string(),
    agentUuid: z.string().min(1, t("form.agentRequired")),
    promptTemplate: z.string(),
    autoSubmit: z.boolean(),
  });

  const form = useAppForm({
    defaultValues: {
      label: action?.label ?? "",
      description: action?.description ?? "",
      agentUuid: action ? (action.agentUuid ?? ASSISTANT) : "",
      promptTemplate: action?.promptTemplate ?? "",
      autoSubmit: action?.autoSubmit ?? false,
    } as QuickActionValues,
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      try {
        await onSave({ ...value, label: value.label.trim() });
        toast.success(t("saved"));
        onClose();
      } catch (error) {
        toast.error(errorMessage(error, t("failed")));
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={action ? t("form.titleEdit") : t("form.titleNew")}
      actions={
        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(submitting) => (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t("form.cancel")}
              </Button>
              <Button variant="primary" loading={submitting} onClick={() => form.handleSubmit()}>
                {action ? t("form.save") : t("form.create")}
              </Button>
            </>
          )}
        </form.Subscribe>
      }
    >
      <form
        aria-label={action ? t("form.titleEdit") : t("form.titleNew")}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField name="label">
          {(f) => <f.TextField label={t("form.label")} required placeholder={t("form.labelPlaceholder")} />}
        </form.AppField>
        <form.AppField name="description">{(f) => <f.TextField label={t("form.description")} />}</form.AppField>
        <form.AppField name="agentUuid">
          {(f) => (
            <f.SelectField
              label={t("form.agent")}
              required
              placeholder={t("form.agentPlaceholder")}
              options={options}
            />
          )}
        </form.AppField>
        <form.AppField name="promptTemplate">
          {(f) => <f.TextareaField label={t("form.prompt")} placeholder={t("form.promptPlaceholder")} />}
        </form.AppField>
        <form.AppField name="autoSubmit">{(f) => <f.ToggleField label={t("form.autoSubmit")} />}</form.AppField>
      </form>
    </Modal>
  );
}
